import React, { useState } from 'react';
import { NavLink, Link, useRouteMatch } from 'react-router-dom';
import {
  Toolbar,
  Typography,
  Icon,
  MenuList,
  Divider,
  ListItemIcon,
  Drawer,
  ListItemSecondaryAction,
  Badge,
  FormControlLabel,
  FormGroup,
  Switch,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LibraryIcon from '@mui/icons-material/AutoStories';
import ReportsIcon from '@mui/icons-material/Assignment';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { TasksIcon } from 'global/components/PerformIcons';
import { NavRealmProvider, NavRealmControl, NavRealmBanner } from '../NavRealm';
import logo from 'assets/logo.svg';
import ibacoslogo from 'assets/ibacos_innovation.svg';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import {
  NAV_ACTIVE_CLASS,
  NavBarContentActions,
  NavBarContentActionsRow,
  NavBarContentChildren,
  NavBarContentMetaRow,
  NavBarContentRoot,
  NavBarContentRootWrap,
  NavBarGrow,
  NavBarMenuButton,
  NavBarRoot,
  NavBarTaskButton,
  NavCopyright,
  NavHeaderLogo,
  NavHeaderLogoWrapper,
  NavMenuItem,
  NavMenuTitle,
  NavNewLink,
  NavPerformLogo,
  NavTab,
  NavTabIcon,
  NavTabLabel,
  NavTabs,
} from './NavBar.styles';
import { useSelector } from 'react-redux';
import { get, size } from 'global/lib/lodash';
import { userHasPermissions } from 'redux/actions/userLogin';
import useNavSettings from 'global/hooks/useNavSettings';
import { stopProp } from 'global/utils';
import { useView } from 'global/hooks/useView';
import { AlertsDrop } from '../../user/alertsdrop';
import { useHashView } from 'feature/share/useHashView';
import { useScreen } from 'global/styles';

const PRIMARY_PAGES = [
  { name: 'tasks', page: 'tasks', Icon: TasksIcon, navKey: 'tasks' },
  { name: 'assessments', page: 'reports', Icon: ReportsIcon, navKey: 'assessments' },
  { name: 'library', page: 'library', Icon: LibraryIcon, navKey: 'library' },
  { name: 'agent', page: 'agent', Icon: AutoAwesomeIcon, navKey: 'agent' },
];

// Selected when viewing a non-primary page (profile, account, changelog, etc.).
const OVERLAY_TAB = '__overlay__';

export default function NavBar(props) {
  const { pageValue } = props;
  const [drawerOpen, changeDrawerOpen] = useState(false);
  const { nav } = useNavSettings();
  const { active: hashViewActive } = useHashView();
  // down('sm') — not only('xs'): custom theme keys (e.g. sidebar: 300) sit between xs/sm.
  const { isDown: isXs } = useScreen('sm');
  const { isDown: isBelowMd } = useScreen('md');
  const isAssessmentPage = !!useRouteMatch('/reports/assessment/:id');

  const visiblePages = PRIMARY_PAGES.filter((p) => get(nav, p.navKey));
  const isPrimaryPage = visiblePages.some((p) => p.page === pageValue);
  const tabValue = isPrimaryPage ? pageValue : OVERLAY_TAB;

  // PFM-2547: hide the top-level nav entirely when viewing via a share link —
  // the user is locked to a single piece of content and shouldn't see tabs / realm switcher.
  // Also hide on assessment detail below sm so the assessment header owns the viewport.
  if (hashViewActive || (isXs && isAssessmentPage)) return null;

  return (
    <NavRealmProvider>
      <Drawer open={drawerOpen} onClose={() => changeDrawerOpen(false)}>
        <NavHeaderLogoWrapper as={Link} to="/home" onClick={() => changeDrawerOpen(false)}>
          <SidebarLogo />
        </NavHeaderLogoWrapper>
        <Divider />
        <div tabIndex={0} role="button" onKeyDown={() => changeDrawerOpen(false)}>
          <SideList onClick={() => changeDrawerOpen(false)} />
        </div>
        <NavPerformLogo src={logo} alt="logo" />
        <NavCopyright>
          <NavNewLink href="https://docs.performwithibacos.com/faq" target="_blank" rel="noopener">
            <NavTabIcon as={Icon}>help</NavTabIcon>
            <span>Need Help?</span>
          </NavNewLink>
          <NavNewLink as={Link} to="/changelog">
            <NavTabIcon as={NewReleasesIcon} />
            <span>What's New</span>
          </NavNewLink>
          <p>
            &copy; {new Date().getFullYear()} IBACOS Perform.
            <br />
            Created with PERFORM<sup>®</sup>
          </p>
        </NavCopyright>
      </Drawer>
      <NavBarRoot position="static" navHidden={!!props.hide}>
        <Toolbar>
          <NavBarMenuButton onClick={() => changeDrawerOpen(true)} sidebar={!!props.sidebar} color="inherit" aria-label="Menu" size="large">
            <MenuIcon />
          </NavBarMenuButton>
          <NavTabs value={tabValue}>
            {visiblePages.map((item) => (
              <NavTab
                key={item.page}
                data-testid={`navbar-tab-${item.page}`}
                // Below md the tabs are display-only wayfinding; clicks are no-ops.
                {...(isBelowMd ? { component: 'div' } : { component: Link, to: `/${item.page}` })}
                label={
                  <>
                    <NavTabIcon as={item.Icon} />
                    <NavTabLabel>{item.name}</NavTabLabel>
                  </>
                }
                value={item.page}
              />
            ))}
            {!isPrimaryPage && (
              <NavTab
                className="nav-tab-overlay"
                component="div"
                value={OVERLAY_TAB}
                label={props.pageTitle}
              />
            )}
          </NavTabs>
          <NavBarGrow />
          <NavRealmControl />
          <AlertsDrop />
        </Toolbar>
        <NavRealmBanner />
      </NavBarRoot>
    </NavRealmProvider>
  );
}

export function NavBarContent(props) {
  const { actions, children } = props;
  // PFM-2547: hashed share-link viewers can't navigate away, so suppress the close button.
  const { active: hashViewActive } = useHashView();

  return (
    <NavBarContentRootWrap>
      <NavBarContentRoot position="static">
        <Toolbar>
          {!hashViewActive && (
            <NavBarMenuButton
              component={Link}
              to={typeof props.link === 'string' ? `/${props.link}` : props.link}
              color="inherit"
              aria-label="Close"
              {...(props.closeProps || {})}
              size="large"
            >
              <span>
                <ArrowBackIcon sx={{ display: { xs: 'inline-flex', lg: 'none' } }} />
                <CloseIcon sx={{ display: { xs: 'none', lg: 'inline-flex' } }} />
              </span>
            </NavBarMenuButton>
          )}
          <Typography
            variant="h6"
            sx={{
              color: 'inherit',
              minWidth: 0,
              // PFM-2628: when the action buttons move inline with Location/
              // Status (below lg), let the title fill the header width. Desktop
              // sizing is unchanged.
              ...(props.actionsSlotId && { flexGrow: { xs: 1, lg: 0 } }),
            }}
          >
            {props.pageTitle}
          </Typography>
          {/* PFM-2628: below lg the assessment's actions sit with Location/Status,
              so the grow spacer and reserved overlay space would only steal width
              from the title — hide them at that breakpoint. Other headers are
              unaffected. */}
          <NavBarGrow sx={props.actionsSlotId ? { display: { xs: 'none', lg: 'block' } } : undefined} />
          {actions && <NavBarContentActions>{actions}</NavBarContentActions>}
          {props.space && (
            <NavBarTaskButton
              sx={props.actionsSlotId ? { display: { xs: 'none', lg: 'block' } } : undefined}
            />
          )}
        </Toolbar>
        {/* PFM-2628: portal target for the mobile Share/Download/Print cluster.
            Shares a row with the Location/Status fields (right-aligned) from lg
            down. Consumers (ReportTabs) portal into this node; it is empty/no-op
            otherwise and the cluster itself is hidden at `lg`+ via its own styles. */}
        {(props.actionsSlotId || children) && (
          <NavBarContentMetaRow>
            {children && <NavBarContentChildren>{children}</NavBarContentChildren>}
            {props.actionsSlotId && <NavBarContentActionsRow id={props.actionsSlotId} />}
          </NavBarContentMetaRow>
        )}
      </NavBarContentRoot>
    </NavBarContentRootWrap>
  );
}

const MENUITEMS = [
  { label: 'profile', page: 'profile', icon: <AccountCircleIcon /> },
  {
    label: 'alerts',
    page: 'alerts',
    icon: <NotificationsActiveIcon />,
    useSecondaryAction: (p) => {
      const view = useSelector((state) => get(state, 'view'));
      const amt = size(get(view, 'alerts.new'));
      if (amt > 0) {
        return <Badge badgeContent={amt >= 25 ? '25+' : amt} color={'primary'} />;
      }
    },
  },
  { label: 'manage users', page: 'users/manage', icon: <SupervisedUserCircleIcon />, disabled: () => !userHasPermissions(['manageUsers']) },
  { label: 'account settings', page: 'account/settings', icon: <SettingsIcon />, disabled: () => !userHasPermissions(['manageAccount']) },
];

function SideList(props) {
  const { nav } = useNavSettings();
  const { view, toggleTheme } = useView();

  const toggleDark = (e) => {
    stopProp(e);
    toggleTheme();
  };

  return (
    <div onClick={props.onClick} style={{ width: 250 }}>
      <MenuList>
        {PRIMARY_PAGES.filter((p) => get(nav, p.navKey)).map((item) => (
          <NavMenuItem component={NavLink} activeClassName={NAV_ACTIVE_CLASS} to={`/${item.page}`} key={item.name} data-testid={item.name}>
            <ListItemIcon>
              <item.Icon />
            </ListItemIcon>
            <NavMenuTitle primary={item.name} />
          </NavMenuItem>
        ))}
      </MenuList>
      <Divider />
      <MenuList>
        {MENUITEMS.map((text) => (
          <NavMenuItem component={NavLink} activeClassName={NAV_ACTIVE_CLASS} to={`/${text.page}`} key={text.page} disabled={get(text, 'disabled', () => false)()}>
            <ListItemIcon>{text.icon}</ListItemIcon>
            <NavMenuTitle primary={text.label} />
            {text.useSecondaryAction && <ListItemSecondaryAction>{text.useSecondaryAction(props)}</ListItemSecondaryAction>}
          </NavMenuItem>
        ))}
      </MenuList>
      <Divider />
      <MenuList>
        <NavMenuItem component={NavLink} activeClassName={NAV_ACTIVE_CLASS} to="/logout">
          <ListItemIcon>
            <ExitToAppIcon />
          </ListItemIcon>
          <NavMenuTitle primary="Logout" />
        </NavMenuItem>
        <NavMenuItem onClick={toggleDark}>
          <FormGroup>
            <FormControlLabel control={<Switch checked={get(view, 'style.palette.mode', 'light') === 'dark'} />} label="Dark Theme" />
          </FormGroup>
        </NavMenuItem>
      </MenuList>
    </div>
  );
}

function SidebarLogo() {
  // PFM-2625: builder logo lives in Redux at view.style.props.logo (set in
  // redux/actions/view.js). Fall back to the IBACOS logo only when no builder
  // logo is set. Mirrors the selector used in feature/report/Tabs/index.js.
  const builderLogo = useSelector((state) => get(state, 'view.style.props.logo'));

  // PFM-2625 follow-up: render the styled <img> directly instead of through EImage
  // (as={Image} unsetWidth). EImage keeps its container at opacity:0 until an
  // async calc() (setTimeout 500ms on load/resize) measures real dimensions. The
  // drawer is a temporary MUI Drawer, so on FIRST open the logo's calc() runs
  // against a not-yet-laid-out element and dims never resolve -> the logo loads
  // but stays invisible until the SECOND open. A plain <img> shows immediately on
  // first open. object-fit:contain preserves aspect ratio (no stretch); the
  // surrounding NavHeaderLogoWrapper centers it via flex.
  return <NavHeaderLogo src={builderLogo || ibacoslogo} alt="" />;
}
